import { useEffect, useRef, useState } from "react";
import PortraitImg from "@/components/panel/PortraitImg";

/**
 * `<LivingPortrait />` — CSS-only cinematic panelist tile that mimics a
 * 6-second looping video without any actual video asset. Composes:
 *   • Ken Burns zoom on the static portrait
 *   • SVG grain overlay with intermittent flicker
 *   • Vertical scanline drift + subtle repeating scanlines
 *   • Radial vignette
 *   • "LIVE · kill%" meta chip + red rec-dot
 *   • Bottom typewriter caption that cycles the panelist's intro_lines
 *
 * Autoplays only when the tile scrolls into view (IntersectionObserver),
 * so five instances on the landing page don't burn cycles below the fold.
 * Respects `prefers-reduced-motion` at the CSS layer.
 */
export default function LivingPortrait({
  portrait,
  name,
  role,
  accent = "#E5FE40",
  introLines = [],
  killRate,
  className = "",
  aspect = "aspect-[4/5]",
  showMeta = true,
  captionSize = "text-[10px]",
  testId,
}) {
  const ref = useRef(null);
  const [live, setLive] = useState(false);
  const [typed, setTyped] = useState("");
  const [lineIdx, setLineIdx] = useState(0);

  const lines = introLines.length
    ? introLines
    : [`${name || "Panelist"} — ${role || "on the panel"}`];
  const line = lines[lineIdx % lines.length] || "";

  useEffect(() => {
    if (!ref.current) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) setLive(e.isIntersecting);
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.15 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!live) return undefined;
    let i = 0;
    setTyped("");
    const typeMs = 34;
    const holdAfterMs = 1600;
    const type = setInterval(() => {
      i += 1;
      setTyped(line.slice(0, i));
      if (i >= line.length) clearInterval(type);
    }, typeMs);
    const advance = setTimeout(
      () => setLineIdx((n) => n + 1),
      Math.max(2200, line.length * typeMs + holdAfterMs)
    );
    return () => {
      clearInterval(type);
      clearTimeout(advance);
    };
  }, [live, lineIdx, line]);

  const tid = testId || `living-portrait-${(name || "panelist").split(" ")[0].toLowerCase()}`;

  return (
    <div
      ref={ref}
      data-testid={tid}
      className={`living-portrait relative overflow-hidden bg-[#050505] ${
        live ? "is-live" : ""
      } ${aspect} ${className}`}
      style={{ "--accent": accent }}
    >
      <PortraitImg
        portrait={portrait}
        alt={`${name} — ${role || "panelist"}`}
        className="living-portrait__img"
        sizes="(max-width: 768px) 90vw, 420px"
        width="800"
        height="1000"
      />
      <div className="living-portrait__scanlines" />
      <div className="living-portrait__scanband" />
      <div className="living-portrait__grain" />
      <div className="living-portrait__vignette" />
      {showMeta && (
        <>
          <span
            className="living-portrait__meta"
            style={{ color: accent, borderColor: `${accent}55` }}
          >
            LIVE{typeof killRate === "number" ? ` · ${killRate}%` : ""}
          </span>
          <span className="living-portrait__dot" aria-hidden="true" />
        </>
      )}
      <div className={`living-portrait__caption ${captionSize}`}>
        <span>{typed}</span>
        <span
          className="living-portrait__caret"
          style={{ background: accent }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}
